import { UserRole, PermissionAction, PermissionsAlias } from './roles'
import { RouteNames, type RouteName } from './route-names'

/**
 * 权限配置项类型
 * 格式: 'RouteName:action'
 * 示例: 'Job:show', 'Job:edit'
 */
export type PermissionKey = `${RouteName}:${PermissionAction}`

/**
 * 生成所有路由的所有操作权限
 * @param routes 要生成权限的路由名称数组（可选，不传则生成所有路由的权限）
 * @param actions 要生成的操作权限数组（可选，不传则生成所有操作）
 * @returns 权限数组
 */
function generateAllPermissions(
  routes?: RouteName[],
  actions?: PermissionAction[]
): PermissionKey[] {
  const targetRoutes = routes || Object.values(RouteNames);
  const targetActions = actions || Object.values(PermissionAction);

  const permissions: PermissionKey[] = [];
  targetRoutes.forEach(route => {
    targetActions.forEach(action => {
      permissions.push(`${route}:${action}` as PermissionKey);
    });
  });

  return permissions;
}

/**
 * 角色权限配置
 * Key: 角色ID
 * Value: 该角色拥有的权限列表，或 'has_all_permission' 表示拥有所有权限
 */
export const RolePermissionConfig: Record<UserRole, PermissionKey[] | 'has_all_permission'> = {
  // 超级管理员 - 自动拥有所有路由的所有权限
  // 未来新增路由时无需手动在这里添加，会自动包含
  [UserRole.SUPER_ADMIN]: PermissionsAlias.hasAllPermission,
  // [UserRole.SUPER_ADMIN]: [
  //   `${RouteNames.Job}:${PermissionAction.SHOW}`,
  //   `${RouteNames.Job}:${PermissionAction.EDIT}`,

  //   `${RouteNames.Job}:${PermissionAction.SHOW}`,
  //   `${RouteNames.Job}:${PermissionAction.EDIT}`,
    
  //   `${RouteNames.JobDetail}:${PermissionAction.SHOW}`,
  //   `${RouteNames.JobDetail}:${PermissionAction.EDIT}`,
  //   `${RouteNames.JobForm}:${PermissionAction.SHOW}`,
  //   `${RouteNames.JobForm}:${PermissionAction.EDIT}`,
  //   `${RouteNames.JobFormEdit}:${PermissionAction.SHOW}`,
  //   `${RouteNames.JobFormEdit}:${PermissionAction.EDIT}`,
    
  //   `${RouteNames.CandidateSelecting}:${PermissionAction.SHOW}`,
  //   `${RouteNames.CandidateSelecting}:${PermissionAction.EDIT}`,
  //   `${RouteNames.CandidateSelecting}:${PermissionAction.DOWNLOAD}`,
  //   `${RouteNames.CandidateSelectingDetail}:${PermissionAction.SHOW}`,
  //   `${RouteNames.CandidateSelectingDetail}:${PermissionAction.EDIT}`,
  //   `${RouteNames.CandidateSelectingDetail}:${PermissionAction.DOWNLOAD}`,

  // ],

  // 管理员
  [UserRole.RECRUITMENT_MANAGER]: [
    // 职位管理 - 查看所有人创建的职位，可管理
    `${RouteNames.Job}:${PermissionAction.SHOW}`,
    `${RouteNames.Job}:${PermissionAction.EDIT}`,
    `${RouteNames.JobDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.JobDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.JobForm}:${PermissionAction.SHOW}`,
    `${RouteNames.JobForm}:${PermissionAction.EDIT}`,
    `${RouteNames.JobFormEdit}:${PermissionAction.SHOW}`,
    `${RouteNames.JobFormEdit}:${PermissionAction.EDIT}`,

    // 选考中候选人 - 查看所有人创建的职位的选考记录，可管理，可下载
    `${RouteNames.CandidateSelecting}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelecting}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateSelecting}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.DOWNLOAD}`,

    // 注册候选人 - 查看所有人添加的注册候选人，可管理，可下载
    `${RouteNames.CandidateRegistered}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateRegistered}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateRegistered}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateEdit}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateEdit}:${PermissionAction.EDIT}`,

    // 企業情報 - 查看和编辑本企业信息
    `${RouteNames.CompanySetting}:${PermissionAction.SHOW}`,
    `${RouteNames.CompanySetting}:${PermissionAction.EDIT}`,

    // 账号管理 - 查看所有账号，管理除自己以外的账号
    `${RouteNames.Account}:${PermissionAction.SHOW}`,
    `${RouteNames.Account}:${PermissionAction.EDIT}`,
    `${RouteNames.AccountDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.AccountDetail}:${PermissionAction.EDIT}`,

    // 勤務先管理 - 查看和管理所有勤務先信息
    `${RouteNames.DepartmentSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.DepartmentSetting}:${PermissionAction.EDIT}`,

    // 选考设置 - 查看和管理选考流程、申请渠道来源
    `${RouteNames.InterviewSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcess}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcess}:${PermissionAction.EDIT}`,
    `${RouteNames.InterviewProcessDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcessDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.InterviewChannel}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewChannel}:${PermissionAction.EDIT}`,

    // 消息中心 - 查看消息
    `${RouteNames.Notify}:${PermissionAction.SHOW}`,
    `${RouteNames.NotifySettings}:${PermissionAction.SHOW}`,
    `${RouteNames.NotifySettings}:${PermissionAction.NOTIFY_SETTING}`,
    
    `${RouteNames.AgentManage}:${PermissionAction.SHOW}`,
    `${RouteNames.AgentManage}:${PermissionAction.EDIT}`,
    // 数据看板
    `${RouteNames.DataOverview}:${PermissionAction.SHOW}`,
    `${RouteNames.DataOverview}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.HeadhunterBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.HeadhunterBoard}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.InternalBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.InternalBoard}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.MyBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.MyBoard}:${PermissionAction.DOWNLOAD}`,

    // 沟通 - 查看聊天页面
    `${RouteNames.Chat}:${PermissionAction.SHOW}`,
  ],

  // hr
  [UserRole.RECRUITMENT_SPECIALIST]: [
    // 职位管理 - 查看所有人创建的职位，可管理
    `${RouteNames.Job}:${PermissionAction.SHOW}`,
    `${RouteNames.Job}:${PermissionAction.EDIT}`,
    `${RouteNames.JobDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.JobDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.JobForm}:${PermissionAction.SHOW}`,
    `${RouteNames.JobForm}:${PermissionAction.EDIT}`,
    `${RouteNames.JobFormEdit}:${PermissionAction.SHOW}`,
    `${RouteNames.JobFormEdit}:${PermissionAction.EDIT}`,

    // 选考中候选人 - 查看所有人创建的职位的选考记录，可管理，可下载
    `${RouteNames.CandidateSelecting}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelecting}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateSelecting}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.DOWNLOAD}`,

    // 注册候选人 - 查看所有人添加的注册候选人，可管理，可下载
    `${RouteNames.CandidateRegistered}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateRegistered}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateRegistered}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.CandidateDetail}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.CandidateEdit}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateEdit}:${PermissionAction.EDIT}`,

    // 企業情報 - 仅可查看本企业信息
    `${RouteNames.CompanySetting}:${PermissionAction.SHOW}`,

    // 账号管理 - 查看所有账号（无管理权限）
    `${RouteNames.Account}:${PermissionAction.SHOW}`,
    `${RouteNames.AccountDetail}:${PermissionAction.SHOW}`,

    // 勤務先管理 - 查看和管理所有勤務先信息
    `${RouteNames.DepartmentSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.DepartmentSetting}:${PermissionAction.EDIT}`,

    // 选考设置 - 仅查看选考流程和申请渠道来源
    `${RouteNames.InterviewSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcess}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcessDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewChannel}:${PermissionAction.SHOW}`,

    // 消息中心 - 查看消息
    `${RouteNames.Notify}:${PermissionAction.SHOW}`,
    `${RouteNames.NotifySettings}:${PermissionAction.SHOW}`,

    `${RouteNames.AgentManage}:${PermissionAction.SHOW}`,
    // 数据看板
    `${RouteNames.DataOverview}:${PermissionAction.SHOW}`,
    `${RouteNames.DataOverview}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.HeadhunterBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.HeadhunterBoard}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.InternalBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.InternalBoard}:${PermissionAction.DOWNLOAD}`,
    `${RouteNames.MyBoard}:${PermissionAction.SHOW}`,
    `${RouteNames.MyBoard}:${PermissionAction.DOWNLOAD}`,

    // 沟通 - 查看聊天页面
    `${RouteNames.Chat}:${PermissionAction.SHOW}`,
  ],

  // 面试官 - 仅可查看参与面试的职位和候选人，可编辑面试结果
  [UserRole.INTERVIEWER]: [
    // 职位管理 - 仅可查看参与面试的职位（需业务逻辑过滤）
    `${RouteNames.Job}:${PermissionAction.SHOW}`,
    `${RouteNames.JobDetail}:${PermissionAction.SHOW}`,

    // 选考中候选人 - 仅可查看参与面试的候选人，可编辑面试结果（需业务逻辑过滤）
    `${RouteNames.CandidateSelecting}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.CandidateSelectingDetail}:${PermissionAction.EDIT}`, // 仅可编辑面试结果

    // 沟通 - 查看聊天页面
    `${RouteNames.Chat}:${PermissionAction.SHOW}`,

    // 消息中心 - 查看消息
    `${RouteNames.Notify}:${PermissionAction.SHOW}`,
    `${RouteNames.NotifySettings}:${PermissionAction.SHOW}`,

    // 企業情報 - 仅可查看本企业信息
    `${RouteNames.CompanySetting}:${PermissionAction.SHOW}`,

  ],
  // 运营支持角色
  [UserRole.ROLE_BUSINESS_OPERATION]: [
    // 企業情報 - 仅可查看本企业信息
    `${RouteNames.CompanySetting}:${PermissionAction.SHOW}`,

    // 职位管理 - 可查看参与面试的职位
    `${RouteNames.Job}:${PermissionAction.SHOW}`,
    `${RouteNames.Job}:${PermissionAction.EDIT}`,
    `${RouteNames.JobDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.JobDetail}:${PermissionAction.EDIT}`,
    `${RouteNames.JobForm}:${PermissionAction.SHOW}`,
    `${RouteNames.JobForm}:${PermissionAction.EDIT}`,

    // 勤務先管理 - 查看和管理所有勤務先信息
    `${RouteNames.DepartmentSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.DepartmentSetting}:${PermissionAction.EDIT}`,


    // 选考设置 - 查看和管理选考流程
    `${RouteNames.InterviewSetting}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcess}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcess}:${PermissionAction.EDIT}`,
    `${RouteNames.InterviewProcessDetail}:${PermissionAction.SHOW}`,
    `${RouteNames.InterviewProcessDetail}:${PermissionAction.EDIT}`,
  ]
}
